import React, { useState } from 'react';

function CompanyTab({ companies, onEdit, onDelete, onNew }) {
  return (
    <>
      <header className="courses-header">
        <div>
          <h1>Företag (Kursportal)</h1>
          <p>Hantera företag med egna kursportaler</p>
        </div>
        <button className="btn btn-primary" onClick={onNew}>
          + Nytt företag
        </button>
      </header>

      <div className="courses-table-container">
        <table className="courses-table">
          <thead>
            <tr>
              <th>Företag</th>
              <th>Org.nr</th>
              <th>Kontaktperson</th>
              <th>Portal-slug</th>
              <th>Status</th>
              <th>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {companies.map(company => (
              <tr key={company.id}>
                <td className="course-name-cell">{company.name}</td>
                <td>{company.organizationNumber || '-'}</td>
                <td>{company.contactPerson || '-'}</td>
                <td>
                  {company.portalSlug ? (
                    <a href={`https://boka.projektledarpodden.se/portal/${company.portalSlug}`} target="_blank" rel="noreferrer">
                      {company.portalSlug}
                    </a>
                  ) : '-'}
                </td>
                <td>
                  <span className={`status-badge ${company.isActive ? 'active' : 'inactive'}`}>
                    {company.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td>
                  <div className="table-actions">
                    <button className="btn btn-secondary btn-small" onClick={() => onEdit(company)}>
                      Redigera
                    </button>
                    <button className="btn btn-danger btn-small" onClick={() => onDelete(company)}>
                      Ta bort
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {companies.length === 0 && (
          <div className="no-data">
            <p>Inga företag registrerade.</p>
            <p>Klicka på "+ Nytt företag" för att lägga till ett.</p>
          </div>
        )}
      </div>
    </>
  );
}

function generateSlug(name) {
  return name
    .toLowerCase()
    .replace(/å/g, 'a').replace(/ä/g, 'a').replace(/ö/g, 'o')
    .replace(/é/g, 'e').replace(/ü/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

function CompanyModal({ show, editing, form, setForm, onSubmit, onClose }) {
  const [copied, setCopied] = useState(false);

  if (!show) return null;

  const portalUrl = form.portalSlug
    ? `https://boka.projektledarpodden.se/portal/${form.portalSlug}`
    : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAutoSlug = () => {
    if (form.name) {
      setForm({ ...form, portalSlug: generateSlug(form.name) });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <h2>{editing ? 'Redigera företag' : 'Lägg till nytt företag'}</h2>
        <form onSubmit={onSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Företagsnamn *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="T.ex. Volvo Group"
              />
            </div>
            <div className="form-group">
              <label>Organisationsnummer</label>
              <input
                type="text"
                value={form.organizationNumber}
                onChange={(e) => setForm({ ...form, organizationNumber: e.target.value })}
                placeholder="556000-0000"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Portal-slug</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={form.portalSlug}
                onChange={(e) => setForm({ ...form, portalSlug: e.target.value })}
                placeholder="volvo-ai-2026"
                pattern="[a-z0-9-]{3,50}"
                title="3-50 tecken, bara små bokstäver, siffror och bindestreck"
                style={{ flex: 1 }}
              />
              <button type="button" className="btn btn-secondary btn-small" onClick={handleAutoSlug}
                title="Generera slug från företagsnamn">
                Auto
              </button>
            </div>
            {portalUrl && (
              <div style={{ marginTop: '6px', fontSize: '0.85em' }}>
                <span style={{ color: '#666' }}>URL: </span>
                <a href={portalUrl} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                  {portalUrl}
                </a>
                <button type="button" onClick={handleCopy}
                  style={{ marginLeft: '8px', cursor: 'pointer', background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 8px', fontSize: '0.85em' }}>
                  {copied ? 'Kopierad!' : 'Kopiera'}
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Kontaktperson</label>
            <input
              type="text"
              value={form.contactPerson}
              onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
              placeholder="Anna Andersson"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Kontakt-epost</label>
              <input
                type="email"
                value={form.contactEmail}
                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                placeholder="anna@foretag.se"
              />
            </div>
            <div className="form-group">
              <label>Kontakttelefon</label>
              <input
                type="tel"
                value={form.contactPhone}
                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                placeholder="070-123 45 67"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Faktureringsadress</label>
            <textarea
              value={form.billingAddress}
              onChange={(e) => setForm({ ...form, billingAddress: e.target.value })}
              rows="2"
              placeholder="Företagsvägen 1, 411 01 Göteborg"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Faktura-epost</label>
              <input
                type="email"
                value={form.billingEmail}
                onChange={(e) => setForm({ ...form, billingEmail: e.target.value })}
                placeholder="faktura@foretag.se"
              />
            </div>
            <div className="form-group">
              <label>Standard fakturareferens</label>
              <input
                type="text"
                value={form.defaultInvoiceReference}
                onChange={(e) => setForm({ ...form, defaultInvoiceReference: e.target.value })}
                placeholder="PO-12345"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Standard kostnadsställe</label>
            <input
              type="text"
              value={form.defaultCostCenter}
              onChange={(e) => setForm({ ...form, defaultCostCenter: e.target.value })}
              placeholder="T.ex. 4010"
            />
          </div>
          <div className="form-group">
            <label>Anteckningar (interna)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows="3"
              placeholder="Interna noteringar om företaget..."
            />
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Aktivt (visas i företagslistor)
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-success">
              {editing ? 'Spara ändringar' : 'Lägg till företag'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Avbryt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { CompanyTab, CompanyModal };
